import MuleBadge from './MuleBadge';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/loading.json';
import ordersService, { Order, DriverValidationLegContextItem } from '../services/orders.service';
import { EnrichedDriver, DriverDrawer } from './DriverDrawer';
import { DriverCompactPopup } from './DriverCompactPopup';
import { Tooltip } from './ui/Tooltip';
import { useJsApiLoader } from '@react-google-maps/api';
import { getGoogleMapsLoaderOptions } from '../config/googleMaps';
import api from '../services/api';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { useIsDisabledByPermission } from '../contexts/PermissionAccessContext';
import { FieldNameBadge } from './FieldNameBadge';
import { DriverIconLinks } from './DriverIconLinks';
import { legIsInTodaysMoves } from '../utils/driverMovesWindow';
import ModalHeader from './ui/ModalHeader';
import { useModalDrag } from '../hooks/useModalDrag';

interface ActiveUser {
  sid: string;
  user_name: string;
  user_id: string;
  user_email?: string;
}

interface DriverSelectorCellProps {
  pronumber: number;
  legnum: number;
  driver?: string;
  originAppointmentDate?: string;
  destinationAppointmentDate?: string;
  /**
   * The leg's destination depart stamp (v1 `legscurrent.departdatetime`, i.e.
   * `OrderLeg.destDepartDateTime`). Only used to decide whether the moves icon
   * is offered: a past-appt leg that hasn't departed is still in the driver's
   * queue today, so it keeps the link. Omitting it fails open (treated as not
   * departed) for grids whose rows don't carry the stamp.
   */
  legDepartDateTime?: string | null;
  originCoordinates?: string;
  destinationCoordinates?: string;
  originName?: string;
  destinationName?: string;
  customerName?: string;
  hazmat?: string;
  isDaycab?: string;
  isTank?: string;
  overweightPermitRequired?: string;
  onDriverAssigned?: (pronumber: number, legnum: number, driver: string) => void;
  // Confirm-aware clear: invoked when the user explicitly clears a driver (X button or
  // empty-input blur). The cell eagerly clears its local state for UI responsiveness,
  // then hands the parent a `revert` it can call if it ends up rejecting the clear
  // (e.g., the user cancels a "this will drop arrival/depart times" confirmation).
  // When omitted, the cell falls back to onDriverAssigned('') with no revert support.
  onClearAttempt?: (pronumber: number, legnum: number, revert: () => void) => void;
  // Note: onValidationChange is intentionally NOT used to avoid infinite re-render loops
  // The validation status is only used locally to show the visual indicator (green/red/loading)
  onValidationChange?: (pronumber: number, legnum: number, isQualified: boolean | null, rejectionReasons?: string[]) => void;
  disabled?: boolean;
  className?: string;
  allowMissingDates?: boolean; // If true, don't disable input when appointment dates are missing
  initialValidationStatus?: 'valid' | 'invalid' | null; // Pre-computed validation status (e.g., from suggestions)
  initialRejectionReasons?: string[]; // Cached rejection reasons from parent state (survives remount)

  // Real-time collaboration props (optional)
  socket?: any;
  isConnected?: boolean;
  broadcastUpdate?: (type: string, payload: any) => void;
  activeDriverAssignments?: Record<string, ActiveUser>;
  activeDriverTyping?: Record<string, string>;
  currentUser?: {
    name?: string;
    username?: string;
    localAccountId?: string;
    homeAccountId?: string;
  };

  // Drag-to-fill props (optional)
  tableType?: string; // e.g., 'imports', 'exports' - used for key generation
  dragFillActive?: boolean; // Whether a drag operation is in progress
  dragFillTargets?: Set<string>; // Keys of inputs that should be highlighted
  onDragMouseDown?: (key: string, value: string, y: number) => void;
  onDragMouseOver?: (key: string, y: number) => void;

  // Warn-and-confirm messages shown before assigning a non-empty driver. Parent
  // pre-computes the list (e.g. via needsDropReleaseWarning()); each entry renders
  // as one line in a single confirmation dialog. Empty/undefined skips the prompt.
  dispatchWarnings?: string[];
  dispatchBlockedReason?: string | null;

  // Tooltip text for an at-a-glance "driver mismatch across dispatch group"
  // indicator. Set by parents that detect inconsistent driver assignments
  // among legs in the same group (e.g. via describeDispatchGroupDriverConflict).
  // Empty/undefined hides the indicator.
  groupDriverConflictTooltip?: string;

  // Dispatch status overlay (for batch dispatch results)
  dispatchStatus?: 'success' | 'error' | 'pending';
  dispatchErrorReason?: string;

  // Ref-based validation reporting (avoids infinite re-render loops unlike onValidationChange)
  parentValidationRef?: React.RefObject<Record<string, 'valid' | 'invalid' | 'loading' | null>>;
  // Ref-based rejection reasons cache — survives cell remounts so tooltip shows specific reasons
  parentRejectionReasonsRef?: React.RefObject<Record<string, string[]>>;
  // Optional: provide effective leg context (current orig/dest, dates, legdrop for every leg
  // in the order) at validation-time. When set and returning a non-empty array, the cell
  // POSTs to validate-driver with that context so qualification reflects unsaved edits
  // (new legs, renumbered legs, edited locations or appt dates) rather than legscurrent.
  // Currently used only by the Order Details modal. Cache is bypassed when this is set.
  getLegContext?: () => DriverValidationLegContextItem[] | null;

  // Optional: provide pre-save order-field overrides at validation-time. When
  // present (non-null, non-empty object), the cell forwards them through the
  // POST validate-driver path so the qualification check uses the edited state
  // — primarily for overweight checks where `overweightpermitrequired` /
  // `overweightapprovedby` may differ from the persisted values. Keys must
  // match the lowercase column names used by the backend's order dict (see
  // driver_dispatch.get_order_details). Requires `getLegContext` to also be
  // wired since order_overrides only flows through the POST endpoint.
  getOrderOverrides?: () => Record<string, unknown> | null;

  // Optional: a value-equality key (e.g. JSON.stringify of the override
  // payload) that triggers re-validation when it changes. The cell normally
  // re-validates only when `driver` / `pronumber` / `legnum` change — this
  // prop lets the parent force a re-check when the order-level state that
  // drives the override changes (e.g. weight crossed the overweight
  // threshold). Stable strings produce no re-run; changes do.
  revalidationKey?: string;

  // Ref-based pending input cache — lets parents preserve uncommitted typed driver values
  // across polling-driven re-merges by reading the ref in their merge function.
  parentDriverInputRef?: React.RefObject<Record<string, { value: string; timestamp: number }>>;

  // Legnums of OTHER legs in this row's dispatch group whose appt dates are blank.
  // The qualifying leg's own dates may be present, but a sibling leg in the same group
  // can lack dates — in that case dispatching one without the other breaks the group,
  // so the parent passes those legnums here and we surface the same red/disabled UI as
  // if this leg's own dates were missing.
  groupMissingDateLegs?: number[];

  /**
   * When set, renders a shield-check indicator next to the validation icon
   * with a tooltip describing the recorded override approval (user, date,
   * reason). Pro details wires this from `dispatch_override_approvals` so
   * an at-a-glance signal appears on legs whose currently-assigned driver
   * was previously justified via the override prompt.
   *
   * `legnum` is the originating leg's CURRENT legnum (the cell that owned
   * the driver field when the approval was granted). When it differs from
   * this cell's `legnum` prop, the tooltip appends "(leg N)" so the
   * dispatcher can see the approval is inherited from a sibling leg in
   * the same dispatch scope.
   */
  overrideApproval?: {
    username: string;
    created_at: string;
    reason: string;
    legnum?: number;
  };

  /**
   * Optional outside-carrier pseudo-driver code (the carrier's SCAC, or the
   * first 6 alphanumeric chars of the carrier name). When set and non-empty:
   *   - it's offered as a selectable option next to the driver input, and
   *   - a driver value equal to it is treated as VALID without a qualification
   *     API call (it's not a real unit, so daycab/hazmat/etc. checks don't apply).
   * Absent/empty ⇒ behavior is entirely unchanged (additive prop).
   */
  extraDriverOption?: string;
}

export const DriverSelectorCell: React.FC<DriverSelectorCellProps> = ({
  pronumber,
  legnum,
  driver = '',
  originAppointmentDate,
  destinationAppointmentDate,
  legDepartDateTime,
  originCoordinates,
  destinationCoordinates,
  originName,
  destinationName,
  customerName,
  hazmat,
  isDaycab,
  isTank,
  overweightPermitRequired,
  onDriverAssigned,
  onClearAttempt,
  onValidationChange, // Used to notify parent of validation result so it persists across remounts
  disabled = false,
  className = '',
  allowMissingDates = false,
  initialValidationStatus = null,
  initialRejectionReasons,
  socket,
  isConnected = false,
  broadcastUpdate,
  activeDriverAssignments = {},
  activeDriverTyping = {},
  currentUser,
  // Drag-to-fill props
  tableType,
  dragFillActive = false,
  dragFillTargets,
  onDragMouseDown,
  onDragMouseOver,
  // Warn-and-confirm messages
  dispatchWarnings,
  dispatchBlockedReason,
  // Driver-mismatch indicator
  groupDriverConflictTooltip,
  // Dispatch status overlay
  dispatchStatus,
  dispatchErrorReason,
  // Validation ref reporting
  parentValidationRef,
  parentRejectionReasonsRef,
  parentDriverInputRef,
  getLegContext,
  getOrderOverrides,
  revalidationKey,
  groupMissingDateLegs,
  overrideApproval,
  extraDriverOption,
}) => {
  // Disable interactive elements when wrapped in a PermissionsContainer at access <= 1
  // (View). Returns false outside any container, so this is backward-compatible with the
  // many existing call sites that don't wrap the cell.
  const disabledByPermission = useIsDisabledByPermission();

  // Outside-carrier pseudo-driver: a non-empty extraDriverOption is a selectable
  // driver value that is NOT a real unit, so it bypasses the qualification API
  // and reports as valid whenever the current driver equals it (case-insensitive,
  // trimmed — matching how the value is derived and typed).
  const extraDriverCode = (extraDriverOption ?? '').trim();
  const isExtraDriverValue = (value: string | undefined): boolean =>
    extraDriverCode !== '' &&
    (value ?? '').trim().toUpperCase() === extraDriverCode.toUpperCase();

  // Picks between the GET validate-driver (DB-resolved leg) and the POST variant
  // (frontend-supplied leg context) based on whether getLegContext is wired and
  // returns a non-empty list. Defining at component scope so both call sites share
  // the choice without diverging.
  const runDriverValidation = (unitNumber: string, signal?: AbortSignal) => {
    const ctx = getLegContext?.();
    if (ctx && ctx.length > 0) {
      const overrides = getOrderOverrides?.();
      const overridesNonEmpty = overrides && Object.keys(overrides).length > 0
        ? overrides
        : undefined;
      return ordersService.validateDriverWithContext(
        pronumber, unitNumber, legnum, ctx, true, overridesNonEmpty, signal,
      );
    }
    return ordersService.validateDriver(pronumber, unitNumber, legnum, true, signal);
  };

  // Race-safety for the two validation effects below. Rapid order edits queue
  // multiple in-flight fetches; out-of-order completion (entirely possible on
  // HTTP/1.1 with parallel connections, or under back-pressure) would let a
  // stale response overwrite a fresh one — so the cell could show a stale
  // valid/invalid state that doesn't match the latest edits.
  //
  // The sequence counter (validationFetchSeqRef) tags every fetch; the
  // response handler ignores anything older than the latest tag.
  // The abort controller (validationAbortRef) cancels any in-flight request
  // when a new one starts, so we don't waste bandwidth on stale fetches
  // either. applyValidationError below skips abort-shaped errors so a
  // cancelled fetch doesn't flip the cell to invalid.
  const validationFetchSeqRef = useRef(0);
  const validationAbortRef = useRef<AbortController | null>(null);
  const startValidationFetch = (): { seq: number; signal: AbortSignal } => {
    validationAbortRef.current?.abort();
    const controller = new AbortController();
    validationAbortRef.current = controller;
    validationFetchSeqRef.current += 1;
    return { seq: validationFetchSeqRef.current, signal: controller.signal };
  };
  const isLatestValidationFetch = (seq: number): boolean =>
    seq === validationFetchSeqRef.current;
  // axios surfaces aborted requests as either `CanceledError` (newer) or
  // an error with name === 'AbortError' / code === 'ERR_CANCELED' (older
  // and fetch-style). Match all three to stay robust across versions.
  const isAbortError = (err: unknown): boolean => {
    if (!err || typeof err !== 'object') return false;
    const e = err as { name?: string; code?: string };
    return e.name === 'CanceledError' || e.name === 'AbortError' || e.code === 'ERR_CANCELED';
  };

  // Shared handler that applies a runDriverValidation response to local +
  // parent-ref state. Lets the multiple validation triggers (driver prop
  // change, revalidationKey bump) reuse one code path so there's a single
  // source of truth for "what does a validation result mean."
  //
  // `seq` is the tag returned by startValidationFetch — if a newer fetch
  // has started since this one was issued, the response is dropped so a
  // stale completion can't overwrite the latest state.
  const applyValidationResponse = (
    response: Awaited<ReturnType<typeof runDriverValidation>>,
    driverUnit: string,
    seq: number,
  ) => {
    if (!isLatestValidationFetch(seq)) return;
    const refKey = `${pronumber}-${legnum}`;
    if (response.drivers && response.drivers.length > 0) {
      const driverInfo = response.drivers[0];
      if (driverInfo.driverName) setDriverName(driverInfo.driverName);
      setMuleUnit(driverInfo.isMule || driverInfo.attributes?.isMule ? driverUnit : null);
      const isValid = driverInfo.isQualified;
      const newStatus = isValid ? 'valid' : 'invalid';
      validationStatusRef.current = newStatus;
      if (parentValidationRef) parentValidationRef.current[refKey] = newStatus;
      setValidationStatus(newStatus);
      if (!isValid && driverInfo.missingRequirements) {
        setRejectionReasons(driverInfo.missingRequirements);
        if (parentRejectionReasonsRef) parentRejectionReasonsRef.current[refKey] = driverInfo.missingRequirements;
      } else {
        setRejectionReasons([]);
        if (parentRejectionReasonsRef) delete parentRejectionReasonsRef.current[refKey];
      }
      if (onValidationChange) onValidationChange(pronumber, legnum, isValid, !isValid && driverInfo.missingRequirements ? driverInfo.missingRequirements : undefined);
      if (isConnected && broadcastUpdate) {
        broadcastUpdate('driver_validated', { pronumber, legnum, value: driverUnit, validationStatus: newStatus });
      }
    } else {
      validationStatusRef.current = 'invalid';
      if (parentValidationRef) parentValidationRef.current[refKey] = 'invalid';
      setValidationStatus('invalid');
      setRejectionReasons(['Driver not found']);
      if (parentRejectionReasonsRef) parentRejectionReasonsRef.current[refKey] = ['Driver not found'];
      if (onValidationChange) onValidationChange(pronumber, legnum, false, ['Driver not found']);
    }
  };
  const applyValidationError = (err: unknown, seq: number) => {
    // Aborted fetches resolve here via axios's CanceledError — they
    // mean the cell intentionally cancelled this fetch because a newer
    // one started. Don't flip to invalid; the newer fetch's handler
    // will write the authoritative state.
    if (isAbortError(err)) return;
    if (!isLatestValidationFetch(seq)) return;
    const refKey = `${pronumber}-${legnum}`;
    validationStatusRef.current = 'invalid';
    if (parentValidationRef) parentValidationRef.current[refKey] = 'invalid';
    setValidationStatus('invalid');
    setRejectionReasons(['Validation failed']);
    if (parentRejectionReasonsRef) parentRejectionReasonsRef.current[refKey] = ['Validation failed'];
    if (onValidationChange) onValidationChange(pronumber, legnum, false, ['Validation failed']);
  };
  // ============================================================================
  // GOOGLE MAPS
  // ============================================================================
  const [suggestActivated, setSuggestActivated] = useState(false);
  const { isLoaded: isMapLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // ============================================================================
  // FIELD NAME VISIBILITY
  // ============================================================================
  const { showFieldNames, highlightedFieldId } = useDisplayPreferences();

  // ============================================================================
  // STATE
  // ============================================================================
  const [inputValue, setInputValue] = useState(driver);
  const [driverName, setDriverName] = useState<string>('');
  const [muleUnit, setMuleUnit] = useState<string | null>(null);
  // Initialize validation status from prop if provided (e.g., suggestions are pre-validated as 'valid')
  const [validationStatus, setValidationStatus] = useState<'valid' | 'invalid' | 'loading' | null>(initialValidationStatus);
  const [rejectionReasons, setRejectionReasons] = useState<string[]>(initialRejectionReasons || []); // Why driver was rejected
  const [popupOpen, setPopupOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drivers, setDrivers] = useState<EnrichedDriver[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const lastAssignedValueRef = useRef<string>(driver); // Track last assigned value to prevent duplicate assignments

  const dispatchMessages = dispatchBlockedReason ? [dispatchBlockedReason] : dispatchWarnings;

  // Warn-and-confirm dialog state. Generic over warning kind — parent supplies
  // the messages via dispatchWarnings; cell only knows "show, confirm, proceed".
  const [warningConfirmOpen, setWarningConfirmOpen] = useState(false);
  const pendingAssignRef = useRef<{ driver: string } | null>(null);
  const {
    panelRef: warningPanelRef,
    panelStyle: warningPanelStyle,
    dragHandleProps: warningDragHandleProps,
  } = useModalDrag(warningConfirmOpen);

  /**
   * Wraps onDriverAssigned with any warn-and-confirm checks.
   * For non-empty driver values: if dispatchWarnings has entries, shows a single
   * confirmation dialog listing them. For clears (empty string), calls immediately.
   */
  const assignDriverWithWarning = (driverValue: string) => {
    if (!onDriverAssigned) return;
    const hasWarnings = !!dispatchBlockedReason || (!!dispatchWarnings && dispatchWarnings.length > 0);
    if (!driverValue || !hasWarnings) {
      if (driverValue !== lastAssignedValueRef.current) {
        lastAssignedValueRef.current = driverValue;
        onDriverAssigned(pronumber, legnum, driverValue);
      }
      return;
    }
    // Stash the pending assignment and show the dialog
    pendingAssignRef.current = { driver: driverValue };
    setWarningConfirmOpen(true);
  };

  const handleWarningConfirm = () => {
    setWarningConfirmOpen(false);
    const pending = pendingAssignRef.current;
    pendingAssignRef.current = null;
    if (pending && onDriverAssigned && !dispatchBlockedReason) {
      lastAssignedValueRef.current = pending.driver;
      onDriverAssigned(pronumber, legnum, pending.driver);
    }
  };

  const handleWarningCancel = () => {
    setWarningConfirmOpen(false);
    pendingAssignRef.current = null;
    // Reset input to previous value (empty or the prop value)
    setInputValue(driver || '');
    setValidationStatus(null);
    setRejectionReasons([]);
  };

  /**
   * Eagerly clears the cell's local state for an X-click or empty-input-blur, then
   * notifies the parent. If onClearAttempt is wired, the parent receives a `revert`
   * it can invoke to restore everything we just cleared (used when the parent shows
   * a confirmation dialog and the user cancels it). Without onClearAttempt, falls
   * back to a fire-and-forget assignDriverWithWarning('').
   */
  const performClearAttempt = () => {
    const refKey = `${pronumber}-${legnum}`;
    const snapshot = {
      inputValue,
      validationStatus,
      rejectionReasons,
      validationStatusRefValue: validationStatusRef.current,
      parentVal: parentValidationRef ? parentValidationRef.current[refKey] : undefined,
      parentRej: parentRejectionReasonsRef ? parentRejectionReasonsRef.current[refKey] : undefined,
    };

    setInputValue('');
    setDriverName('');
    setValidationStatus(null);
    setRejectionReasons([]);
    validationStatusRef.current = null;
    if (parentValidationRef) parentValidationRef.current[refKey] = null;
    if (parentRejectionReasonsRef) delete parentRejectionReasonsRef.current[refKey];
    handleInputBlurCollaboration();
    if (onValidationChange) onValidationChange(pronumber, legnum, null);

    const revert = () => {
      setInputValue(snapshot.inputValue);
      setValidationStatus(snapshot.validationStatus);
      setRejectionReasons(snapshot.rejectionReasons);
      validationStatusRef.current = snapshot.validationStatusRefValue;
      if (parentValidationRef) {
        if (snapshot.parentVal === undefined) delete parentValidationRef.current[refKey];
        else parentValidationRef.current[refKey] = snapshot.parentVal;
      }
      if (parentRejectionReasonsRef) {
        if (snapshot.parentRej === undefined) delete parentRejectionReasonsRef.current[refKey];
        else parentRejectionReasonsRef.current[refKey] = snapshot.parentRej;
      }
      if (onValidationChange) {
        const isQualified =
          snapshot.validationStatus === 'valid' ? true :
          snapshot.validationStatus === 'invalid' ? false : null;
        onValidationChange(
          pronumber,
          legnum,
          isQualified,
          snapshot.rejectionReasons.length > 0 ? snapshot.rejectionReasons : undefined,
        );
      }
    };

    if (onClearAttempt) {
      onClearAttempt(pronumber, legnum, revert);
    } else {
      assignDriverWithWarning('');
    }
  };

  // Global pin preference - shared across all driver inputs using a static property
  const [isPinned, setIsPinned] = useState(() => {
    // Try user settings first, then localStorage as fallback
    const localSaved = localStorage.getItem('driverDrawer_pinned');
    return localSaved === 'true';
  });

  // Load pin preference from user settings on mount
  // Temporarily disabled to avoid 404 spam until backend endpoint is created
  // useEffect(() => {
  //   const loadPinPreference = async () => {
  //     try {
  //       const setting = await userSettingsService.getUserSetting('driver_drawer_pinned');
  //       if (setting && setting.setting_value !== undefined) {
  //         const pinned = setting.setting_value === true || setting.setting_value === 'true';
  //         console.log('[DriverSelectorCell] Loaded pin preference from DB:', pinned);
  //         setIsPinned(pinned);
  //         // Also update localStorage so other instances can read it immediately
  //         if (pinned) {
  //           localStorage.setItem('driverDrawer_pinned', 'true');
  //         } else {
  //           localStorage.removeItem('driverDrawer_pinned');
  //         }
  //       }
  //     } catch (error) {
  //       // Silently fail - will use localStorage or default (false)
  //       console.log('[DriverSelectorCell] Failed to load pin preference, using localStorage/default');
  //     }
  //   };
  //   loadPinPreference();
  // }, []);

  // Listen for storage changes from other instances
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'driverDrawer_pinned') {
        const pinned = e.newValue === 'true';
        // console.log('[DriverSelectorCell] Pin preference changed by another instance:', pinned);
        setIsPinned(pinned);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Save pin preference to user settings when it changes
  const handlePinChange = async (pinned: boolean) => {
    // console.log('[DriverSelectorCell] Pin state changing to:', pinned);
    setIsPinned(pinned);

    // Update localStorage immediately so all instances on this page update
    if (pinned) {
      localStorage.setItem('driverDrawer_pinned', 'true');
    } else {
      localStorage.removeItem('driverDrawer_pinned');
    }

    // Dispatch custom event to notify other instances on the same page
    window.dispatchEvent(new CustomEvent('driverDrawerPinChanged', { detail: { pinned } }));

    // Save to database for persistence across sessions/devices
    // Temporarily disabled to avoid 404 spam until backend endpoint is created
    // try {
    //   if (pinned) {
    //     await userSettingsService.saveUserSetting('driver_drawer_pinned', true);
    //     console.log('[DriverSelectorCell] Saved pin preference to DB: true');
    //   } else {
    //     // Clear the setting when unpinned
    //     await userSettingsService.deleteUserSetting('driver_drawer_pinned');
    //     console.log('[DriverSelectorCell] Cleared pin preference from DB');
    //   }
    // } catch (error) {
    //   console.error('[DriverSelectorCell] Failed to save pin preference:', error);
    // }
  };

  // Listen for custom event from other instances on the same page
  useEffect(() => {
    const handlePinChanged = (e: any) => {
      const pinned = e.detail.pinned;
      // console.log('[DriverSelectorCell] Pin changed via custom event:', pinned);
      setIsPinned(pinned);
    };

    window.addEventListener('driverDrawerPinChanged', handlePinChanged);
    return () => window.removeEventListener('driverDrawerPinChanged', handlePinChanged);
  }, []);

  // Helper to check if appointment dates are missing
  const isMissingDate = (date: string | undefined) => {
    return !date || date.trim() === '' || date === '0000-00-00' || date === '-';
  };

  // Generate key for drag-to-fill identification
  const dragFillKey = tableType ? `search-${tableType}-order-${pronumber}-${legnum}` : '';
  const isDragFillTarget = dragFillTargets?.has(dragFillKey) ?? false;

  const origDateMissing = isMissingDate(originAppointmentDate);
  const destDateMissing = isMissingDate(destinationAppointmentDate);
  const linkedLegsMissingDates = groupMissingDateLegs ?? [];
  const hasLinkedLegMissingDates = linkedLegsMissingDates.length > 0;
  // Only treat missing dates as a blocking issue if allowMissingDates is false.
  // Linked-leg gaps block dispatch even when this row's own dates are present —
  // dispatching one leg of a group without its sibling leaves the group broken in CMS.
  const hasMissingDates = !allowMissingDates && (origDateMissing || destDateMissing || hasLinkedLegMissingDates);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Update input value when driver prop changes from external source (e.g., drag-to-fill)
   */
  const inputValueRef = useRef(inputValue);
  const validationStatusRef = useRef(validationStatus);
  const prevDriverPropRef = useRef<string | undefined>(undefined); // Track previous driver prop

  // Keep refs updated synchronously during render (not in an effect)
  // This ensures the driver effect sees the latest values when it runs
  inputValueRef.current = inputValue;
  validationStatusRef.current = validationStatus;

  // Sync validation status to parent ref (imperative write avoids re-render loops)
  if (parentValidationRef) {
    const refKey = `${pronumber}-${legnum}`;
    parentValidationRef.current[refKey] = validationStatus;
  }

  // Sync uncommitted typed input to parent ref so polling-driven merges can preserve it.
  // Write whenever the typed value differs from the committed driver prop; clear once they match.
  if (parentDriverInputRef) {
    const refKey = `${pronumber}-${legnum}`;
    if (inputValue && inputValue !== driver) {
      parentDriverInputRef.current[refKey] = { value: inputValue, timestamp: Date.now() };
    } else {
      delete parentDriverInputRef.current[refKey];
    }
  }

  // NOTE: We intentionally do NOT report validation status changes to parent
  // The validation status is only used locally to show the visual indicator (green/red/loading)
  // Reporting to parent was causing infinite loops because:
  // 1. Parent's setDriverQualifications triggers re-render
  // 2. Some callback in useMemo deps gets recreated
  // 3. Columns useMemo re-runs, cells remount
  // 4. Cell validates again, reports to parent, goto 1
  //
  // The parent only needs qualification status for the "Confirm" button logic,
  // but we can let that be handled by just checking if the local cell shows valid/invalid

  useEffect(() => {
    // If driver prop is empty, clear input and validation — UNLESS the user has typed
    // a value that differs from the last seen prop (uncommitted edit in progress).
    // Without this guard, the effect re-fires when initialValidationStatus changes
    // (e.g., null → 'invalid' after the API returns) and wipes the user's typed input.
    if (!driver || driver === '') {
      const previousDriverProp = prevDriverPropRef.current;
      const userHasUncommittedEdit =
        inputValueRef.current !== '' && inputValueRef.current !== (previousDriverProp || '');
      if (userHasUncommittedEdit) {
        prevDriverPropRef.current = driver;
        return;
      }
      if (inputValueRef.current !== '') {
        setInputValue('');
        setValidationStatus(null);
      }
      prevDriverPropRef.current = driver;
      return;
    }

    // Check if driver prop changed from previous value (external change like drag-to-fill)
    const previousDriverProp = prevDriverPropRef.current;
    const driverPropChanged = driver !== previousDriverProp;
    prevDriverPropRef.current = driver;

    // Outside-carrier pseudo-driver wins over any (possibly stale) cached status:
    // it's not a real unit, so mirror the current input and report valid without
    // an API call. Placed before the cache-reuse block so a leftover 'invalid'
    // from a previous real driver can't override it.
    if (isExtraDriverValue(driver)) {
      const refKey = `${pronumber}-${legnum}`;
      if (inputValueRef.current !== driver) setInputValue(driver);
      validationStatusRef.current = 'valid';
      if (parentValidationRef) parentValidationRef.current[refKey] = 'valid';
      setValidationStatus('valid');
      setRejectionReasons([]);
      if (parentRejectionReasonsRef) delete parentRejectionReasonsRef.current[refKey];
      if (onValidationChange) onValidationChange(pronumber, legnum, true);
      return;
    }

    // If parentValidationRef has a cached result for this cell (e.g., from a previous
    // mount before virtualization/pagination unmounted it), reuse it without an API call.
    if (parentValidationRef && driver === inputValueRef.current) {
      const refKey = `${pronumber}-${legnum}`;
      const cachedStatus = parentValidationRef.current[refKey];
      if (cachedStatus === 'valid' || cachedStatus === 'invalid') {
        setValidationStatus(cachedStatus);
        // Restore cached rejection reasons so tooltip shows specifics after remount
        if (cachedStatus === 'invalid' && parentRejectionReasonsRef?.current[refKey]) {
          setRejectionReasons(parentRejectionReasonsRef.current[refKey]);
        }
        return;
      }
    }

    // If driver prop matches our current local value and we have any validation status,
    // skip re-validation entirely. This handles:
    // 1. Cell remount: prevDriverPropRef was undefined, but local state matches
    // 2. Parent re-render: prop hasn't actually changed in value
    //
    // Bypass this skip when getLegContext is provided — the leg context (orig/dest codes,
    // appt dates, legdrop) can change while the driver value stays the same, and a stale
    // cached status from before the edit would lie.
    if (
      driver === inputValueRef.current &&
      validationStatusRef.current !== null &&
      !getLegContext
    ) {
      return;
    }

    // If driver prop changed externally (e.g., drag-to-fill), update input and validate
    if (driverPropChanged) {
      // Skip if validation is in progress - user is editing and we shouldn't overwrite their input
      // This prevents background refresh from reverting manual edits while validation is happening
      if (validationStatusRef.current === 'loading') {
        return;
      }

      // Skip if user has typed a value that differs from what the prop was before
      // This means user is actively editing - don't overwrite their input with a new suggestion
      // Note: previousDriverProp is undefined on first mount, so skip this guard on mount
      if (previousDriverProp !== undefined && inputValueRef.current && inputValueRef.current !== previousDriverProp) {
        return;
      }

      setInputValue(driver);

      // Read-only users (View access on the `leg-driver-field` permission) cannot
      // change the driver, so we never re-check its qualification for them. The
      // displayed value is synced above, but we stop before any validation: a
      // reported `invalid` here feeds ProDetailContainer's save-time override
      // modal and would block the user's save on a pre-existing unqualified
      // driver they can't even edit. Skipping also avoids a 403 for users
      // without the validate-driver endpoint permission (which would otherwise
      // surface as `invalid` via applyValidationError).
      if (disabledByPermission) {
        setValidationStatus(null);
        return;
      }

      // If we have a pre-computed validation status (e.g., from Smart Dispatch suggestions
      // or cached from a previous mount), use it directly instead of making an API call.
      // Only honor it when the driver matches our local value (remount with same driver),
      // not when a new driver was dragged through (which needs fresh validation).
      if (initialValidationStatus !== null && driver === inputValueRef.current) {
        setValidationStatus(initialValidationStatus);
        return;
      }

      // Bypass validation for unit 000 (outside carrier). The outside-carrier
      // pseudo-driver is handled earlier in this effect.
      if (driver.trim() === '000') {
        setValidationStatus('valid');
        return;
      }

      setValidationStatus('loading');

      // Auto-validate the externally set driver value
      const { seq, signal } = startValidationFetch();
      runDriverValidation(driver, signal)
        .then((response) => applyValidationResponse(response, driver, seq))
        .catch((err) => applyValidationError(err, seq));
    }
  }, [driver, pronumber, legnum, initialValidationStatus, extraDriverOption]);

  // Re-validate when `revalidationKey` flips. Parents (LegsGrid) bump this
  // string when any pre-save order edit affects qualification — overweight
  // transitions, chassis-type swap, etc. — so the cell's valid/invalid
  // indicator stays in sync with the requirements set even when `driver`
  // didn't change. Reuses the same runDriverValidation + applyValidation*
  // helpers so there's a single validation code path.
  const isFirstRevalKeyRender = useRef(true);
  useEffect(() => {
    if (isFirstRevalKeyRender.current) {
      isFirstRevalKeyRender.current = false;
      return;
    }
    // Read-only users never re-run driver qualification (see the mount-effect
    // guard above) — bail before hitting the validate-driver endpoint.
    if (disabledByPermission) return;
    if (!driver || driver.trim() === '') return;
    // The pseudo-driver and unit 000 are not real units — stay valid without
    // re-hitting the qualification endpoint when requirements change.
    if (driver.trim() === '000' || isExtraDriverValue(driver)) {
      setValidationStatus('valid');
      return;
    }
    setValidationStatus('loading');
    const { seq, signal } = startValidationFetch();
    runDriverValidation(driver, signal)
      .then((response) => applyValidationResponse(response, driver, seq))
      .catch((err) => applyValidationError(err, seq));
    // applyValidation* / runDriverValidation are defined inline each render and
    // close over the latest props. Intentionally trigger only on key changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revalidationKey]);

  // Distance calculation using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Radius of the Earth in miles
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Fetch qualified drivers
  const fetchDrivers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Forward pre-save order edits (overweight, daycab, OTR, etc.) AND
      // the same leg_context the validate path uses, so both endpoints
      // resolve `destapptdate` (and therefore the cached `is93k` lookup)
      // the same way. Without leg_context the suggest path queries
      // legsall and can land on a different latest-loaded-leg date than
      // the cell-side validate, which would let drivers bypass the
      // county-permit check on Suggest while the cell still showed them
      // invalid.
      const overrides = getOrderOverrides?.();
      const overridesNonEmpty = overrides && Object.keys(overrides).length > 0
        ? overrides
        : undefined;
      const ctx = getLegContext?.();
      const ctxNonEmpty = ctx && ctx.length > 0 ? ctx : undefined;
      // Fetch data in parallel
      const [validationResponse, locationsWithVinsResponse, dispatchResponse] = await Promise.all([
        ordersService.findValidDrivers(pronumber, legnum, overridesNonEmpty, ctxNonEmpty),
        api.get('/samsara/vehicles/locations-with-vins').then(r => r.data),
        ordersService.getLatestDispatches(undefined, undefined)
      ]);

      const qualifiedDrivers = validationResponse.drivers;
      const locationsWithVins = locationsWithVinsResponse.data || [];
      const dispatches = dispatchResponse.drivers || [];

      // Create VIN to location map
      const vinToLocationMap = new Map();
      const vinToDispatcherMap = new Map();
      locationsWithVins.forEach((item: any) => {
        if (item.vin && item.location) {
          vinToLocationMap.set(item.vin, item.location);
        }
        if (item.vin && item.dispatchInfo?.dispatcher) {
          vinToDispatcherMap.set(item.vin, item.dispatchInfo.dispatcher);
        }
      });

      // Try to get actual GPS coordinates from manual arrival time first
      let originLat: number | null = null;
      let originLng: number | null = null;

      try {
        const gpsResponse = await api.get(`/orders/order-gps-coordinates/${pronumber}/${legnum}`);
        const gpsData = gpsResponse.data;
        // Use actual GPS coordinates from manual arrival if available
        if (gpsData.originActualGPS) {
          originLat = gpsData.originActualGPS.latitude;
          originLng = gpsData.originActualGPS.longitude;
          // console.log('[DriverSelectorCell] Using actual GPS from manual arrival:', { originLat, originLng });
        }
      } catch (error) {
        // console.log('[DriverSelectorCell] Could not fetch GPS coordinates, falling back to geofence');
      }

      // Fallback to geofence coordinates if no manual arrival GPS available
      if (originLat === null || originLng === null) {
        if (originCoordinates && originCoordinates.includes(',')) {
          const firstCoordPair = originCoordinates.split(';')[0].trim();
          const coords = firstCoordPair.split(',').map(c => parseFloat(c.trim()));

          if (coords.length >= 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
            originLat = coords[0];
            originLng = coords[1];
            // console.log('[DriverSelectorCell] Using geofence coordinates:', { originLat, originLng });
          }
        }
      }

      // Enrich drivers with location and distance
      const enrichedDrivers: EnrichedDriver[] = qualifiedDrivers.map((driver: any) => {
        const driverVin = driver.truckVin;
        const location = driverVin ? vinToLocationMap.get(driverVin) : null;
        const dispatcher = driverVin ? vinToDispatcherMap.get(driverVin) : null;

        let distance = null;
        if (location && originLat !== null && originLng !== null) {
          distance = calculateDistance(originLat, originLng, location.latitude, location.longitude);
        }

        const dispatchInfo = dispatches.find((d: any) => d.unitNumber === driver.unitNumber);

        return {
          ...driver,
          dispatcher: dispatcher,
          location: location ? {
            latitude: location.latitude,
            longitude: location.longitude,
            speed: location.speed,
            hosStatus: null,
            time: location.time
          } : null,
          distanceFromOrigin: distance,
          dispatchInfo: dispatchInfo?.dispatch ? {
            proNumber: dispatchInfo.dispatch.proNumber,
            isCompleted: dispatchInfo.dispatch.isCompleted,
            originName: dispatchInfo.dispatch.origin?.name || 'Unknown',
            destinationName: dispatchInfo.dispatch.destination?.name || 'Unknown'
          } : null
        };
      });

      // Sort drivers
      enrichedDrivers.sort((a, b) => {
        if (a.isQualified !== b.isQualified) return b.isQualified ? 1 : -1;
        const aAvailable = a.dispatchInfo?.isCompleted !== false;
        const bAvailable = b.dispatchInfo?.isCompleted !== false;
        if (aAvailable !== bAvailable) return bAvailable ? 1 : -1;
        if (a.distanceFromOrigin === null && b.distanceFromOrigin === null) return 0;
        if (a.distanceFromOrigin === null) return 1;
        if (b.distanceFromOrigin === null) return -1;
        return a.distanceFromOrigin - b.distanceFromOrigin;
      });

      setDrivers(enrichedDrivers);
    } catch (err) {
      console.error('Failed to fetch drivers:', err);
      setError('Error loading drivers');
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // REAL-TIME COLLABORATION HELPERS
  // ============================================================================

  /**
   * Get the user info for an input being edited by another user
   */
  const getInputEditorUser = (): { name: string; initials: string } | null => {
    const key = `${pronumber}-${legnum}`;
    const activeUser = activeDriverAssignments[key];
    if (activeUser && activeUser.sid !== socket?.id) {
      const name = activeUser.user_name || 'Unknown User';
      const nameParts = name.split(' ');
      const initials = nameParts.length >= 2
        ? nameParts[0][0] + nameParts[nameParts.length - 1][0]
        : name.substring(0, 2);
      return { name, initials: initials.toUpperCase() };
    }
    return null;
  };

  /**
   * Check if input is being edited by another user
   */
  const isBeingEditedByOther = (): boolean => {
    const key = `${pronumber}-${legnum}`;
    const activeUser = activeDriverAssignments[key];
    return !!(activeUser && activeUser.sid !== socket?.id);
  };

  /**
   * Get the typing value from another user
   */
  const getTypingValue = (): string | null => {
    const key = `${pronumber}-${legnum}`;
    return activeDriverTyping[key] || null;
  };

  /**
   * Handle input focus - broadcast to other users
   */
  const handleInputFocus = () => {
    if (isConnected && socket?.id && broadcastUpdate) {
      broadcastUpdate('driver_assignment_started', {
        pronumber,
        legnum,
        user: {
          sid: socket.id,
          user_name: currentUser?.name || currentUser?.username || 'Anonymous',
          user_id: currentUser?.localAccountId || currentUser?.homeAccountId,
          user_email: currentUser?.username,
        },
      });
    }
  };

  /**
   * Handle input blur - broadcast to other users
   */
  const handleInputBlurCollaboration = () => {
    if (isConnected && socket?.id && broadcastUpdate) {
      broadcastUpdate('driver_assignment_stopped', {
        pronumber,
        legnum,
      });
    }
  };

  // Handle input change (no validation on type)
  const handleInputChange = (value: string) => {
    setInputValue(value);
    // Reset validation status when typing
    setValidationStatus(null);

    // Reset last assigned value when input is cleared
    if (!value) {
      lastAssignedValueRef.current = '';
    }

    // Broadcast typing to other users
    if (isConnected && broadcastUpdate) {
      // console.log('[DriverSelectorCell] Broadcasting driver_typing:', { pronumber, legnum, value });
      broadcastUpdate('driver_typing', {
        pronumber,
        legnum,
        value,
      });
    }
  };

  // Handle Enter key press to save valid driver
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && validationStatus === 'valid' && inputValue) {
      assignDriverWithWarning(inputValue);
      return;
    }

    // Arrow up/down: move focus to the adjacent row's driver input within the
    // same table. Moving focus blurs this input, which fires handleInputBlur and
    // validates the value the user just typed. Gated on tableType so this only
    // affects the multi-row dispatch grids (Dispatch Planner, Imports/Exports
    // cards) whose inputs are tagged with data-table-type.
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && tableType) {
      e.preventDefault();
      // DOM order of these inputs follows the table's rendered (filtered + sorted)
      // row order, so filtered-out rows are naturally skipped. Virtualized tables
      // keep overscan rows mounted, so the immediate neighbour is present for
      // single-step navigation, and scrollIntoView keeps the table following.
      const inputs = Array.from(
        document.querySelectorAll<HTMLInputElement>(
          `input[data-driver-input="true"][data-table-type="${tableType}"]`,
        ),
      );
      const currentIndex = inputs.indexOf(e.currentTarget);
      if (currentIndex === -1) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      // Walk past disabled inputs (missing-date / locked / being-edited rows
      // can't take focus); stop at the first/last input without wrapping.
      for (let i = currentIndex + step; i >= 0 && i < inputs.length; i += step) {
        const next = inputs[i];
        if (!next.disabled) {
          next.focus();
          next.scrollIntoView({ block: 'nearest' });
          break;
        }
      }
    }
  };

  // Handle validation on blur
  const handleInputBlur = async () => {
    // console.log('[DriverSelectorCell] Input blur', { pronumber, legnum, inputValue });

    // Don't broadcast stopped editing yet - we might have a value that keeps the lock
    // We'll unlock in the handlers when popup/drawer closes or when input is cleared

    // Perform validation
    if (inputValue.length > 0) {
      // Commit the typed value to the parent FIRST, synchronously — before any
      // awaited validation. Clicking Save blurs this input, and the parent's
      // Save handler reads leg state on that same click; the driver must reach
      // parent state within this blur event, or an immediate "type-then-Save"
      // saves stale state and drops the driver. Validation below only refreshes
      // the local qualified/unqualified indicator — it never gated this commit
      // (every branch committed regardless of the qualification result).
      assignDriverWithWarning(inputValue);

      // Bypass validation for unit 000 (outside carrier) and the outside-carrier
      // pseudo-driver — neither is a real unit to qualify.
      if (inputValue.trim() === '000' || isExtraDriverValue(inputValue)) {
        const refKey = `${pronumber}-${legnum}`;
        validationStatusRef.current = 'valid';
        if (parentValidationRef) parentValidationRef.current[refKey] = 'valid';
        setValidationStatus('valid');
        setRejectionReasons([]);
        if (parentRejectionReasonsRef) delete parentRejectionReasonsRef.current[refKey];
        if (onValidationChange) {
          onValidationChange(pronumber, legnum, true);
        }
        return;
      }

      // console.log('[DriverSelectorCell] Validating driver:', inputValue);
      setValidationStatus('loading');

      // Race-safety: a rapid edit could trigger one of the validation effects
      // while this blur-driven fetch is still in flight. startValidationFetch
      // aborts any prior in-flight validation and tags this one with a seq —
      // we drop state writes if a newer fetch has superseded us (the value
      // itself was already committed above, so nothing is lost).
      const { seq, signal } = startValidationFetch();

      try {
        // Call the actual validation endpoint
        const response = await runDriverValidation(inputValue, signal);

        // console.log('[DriverSelectorCell] Validation response:', response);

        // Check if any driver was returned and is qualified
        if (response.drivers && response.drivers.length > 0) {
          const driverData = response.drivers[0];
          if (isLatestValidationFetch(seq)) {
            if (driverData.driverName) setDriverName(driverData.driverName);
            setMuleUnit(driverData.isMule || driverData.attributes?.isMule ? driverData.unitNumber : null);
            const isValid = driverData.isQualified;
            const newStatus = isValid ? 'valid' : 'invalid';
            const refKey = `${pronumber}-${legnum}`;
            // Update refs immediately — survives unmount (virtualization scroll-away)
            validationStatusRef.current = newStatus;
            if (parentValidationRef) parentValidationRef.current[refKey] = newStatus;
            setValidationStatus(newStatus);

            // Store rejection reasons if invalid (and cache in parent ref for remount)
            if (!isValid && driverData.missingRequirements) {
              setRejectionReasons(driverData.missingRequirements);
              if (parentRejectionReasonsRef) parentRejectionReasonsRef.current[refKey] = driverData.missingRequirements;
            } else {
              setRejectionReasons([]);
              if (parentRejectionReasonsRef) delete parentRejectionReasonsRef.current[refKey];
            }

            // Report the qualification result to the parent (drives the
            // save-time override prompt and survives remount via the refs above).
            if (onValidationChange) {
              onValidationChange(pronumber, legnum, isValid, !isValid && driverData.missingRequirements ? driverData.missingRequirements : undefined);
            }

            // Broadcast validation result to other users
            if (isConnected && broadcastUpdate) {
              broadcastUpdate('driver_validated', {
                pronumber,
                legnum,
                value: inputValue,
                validationStatus: newStatus,
              });
            }
          }
        } else {
          if (isLatestValidationFetch(seq)) {
            const refKey = `${pronumber}-${legnum}`;
            validationStatusRef.current = 'invalid';
            if (parentValidationRef) parentValidationRef.current[refKey] = 'invalid';
            setValidationStatus('invalid');
            setRejectionReasons(['Driver not found']);
            if (parentRejectionReasonsRef) parentRejectionReasonsRef.current[refKey] = ['Driver not found'];
            // Notify parent of invalid status
            if (onValidationChange) {
              onValidationChange(pronumber, legnum, false, ['Driver not found']);
            }
            // Broadcast invalid result to other users
            if (isConnected && broadcastUpdate) {
              broadcastUpdate('driver_validated', { pronumber, legnum, value: inputValue, validationStatus: 'invalid' });
            }
          }
        }
      } catch (error) {
        // Aborted fetches mean a newer validation has taken over — don't flip
        // to invalid; the newer fetch's handler writes authoritative state.
        // The typed value was already committed synchronously above.
        if (isAbortError(error)) {
          return;
        }
        if (isLatestValidationFetch(seq)) {
          console.error('[DriverSelectorCell] Driver validation failed:', error);
          const refKey = `${pronumber}-${legnum}`;
          validationStatusRef.current = 'invalid';
          if (parentValidationRef) parentValidationRef.current[refKey] = 'invalid';
          setValidationStatus('invalid');
          setRejectionReasons(['Validation failed']);
          if (parentRejectionReasonsRef) parentRejectionReasonsRef.current[refKey] = ['Validation failed'];
          // Notify parent of error (treat as invalid)
          if (onValidationChange) {
            onValidationChange(pronumber, legnum, false, ['Validation failed']);
          }
          if (isConnected && broadcastUpdate) {
            broadcastUpdate('driver_validated', { pronumber, legnum, value: inputValue, validationStatus: 'invalid' });
          }
        }
      }
    } else {
      // Input is empty — same confirm-aware clear path as the X button so a parent
      // confirmation modal can cancel the clear without leaving the cell desynced.
      performClearAttempt();
    }
  };

  // Handle Find button click
  const handleFindClick = () => {
    setSuggestActivated(true);
    fetchDrivers();

    // Broadcast that we're selecting a driver (locks input for others)
    handleInputFocus();

    // If pinned, open drawer directly, otherwise show popup
    if (isPinned) {
      setDrawerOpen(true);
    } else {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPopupPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.right + window.scrollX - 384 // 384px is popup width (w-96)
        });
      }
      setPopupOpen(true);
    }
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Report a popup/drawer-selected driver as validated to the parent. Picks come
   * from the qualified-driver list and the cell shows them as 'valid', but unlike
   * the typed-blur path nothing here calls onValidationChange — so the Dispatch
   * Planner footer counter never learned about suggest-box selections. Mirror the
   * blur path's parent-state writes so the count increments immediately.
   */
  const reportSelectedDriverValidated = (unit: string) => {
    const refKey = `${pronumber}-${legnum}`;
    validationStatusRef.current = 'valid';
    if (parentValidationRef) parentValidationRef.current[refKey] = 'valid';
    setRejectionReasons([]);
    if (parentRejectionReasonsRef) delete parentRejectionReasonsRef.current[refKey];
    if (onValidationChange) onValidationChange(pronumber, legnum, true);
    if (isConnected && broadcastUpdate) {
      broadcastUpdate('driver_validated', { pronumber, legnum, value: unit, validationStatus: 'valid' });
    }
  };

  /**
   * Select the outside-carrier pseudo-driver. It's not a real unit, so we set
   * it valid directly (no qualification API) and commit it via the normal
   * assignment path — mirroring the unit-000 bypass in handleInputBlur.
   */
  const handleSelectExtraDriver = () => {
    if (!extraDriverCode) return;
    const refKey = `${pronumber}-${legnum}`;
    setInputValue(extraDriverCode);
    setDriverName('');
    validationStatusRef.current = 'valid';
    if (parentValidationRef) parentValidationRef.current[refKey] = 'valid';
    setValidationStatus('valid');
    setRejectionReasons([]);
    if (parentRejectionReasonsRef) delete parentRejectionReasonsRef.current[refKey];
    if (onValidationChange) onValidationChange(pronumber, legnum, true);
    if (isConnected && broadcastUpdate) {
      broadcastUpdate('driver_validated', { pronumber, legnum, value: extraDriverCode, validationStatus: 'valid' });
    }
    assignDriverWithWarning(extraDriverCode);
  };

  /**
   * Handle driver selection from compact popup
   */
  const handleDriverSelect = (selectedDriver: EnrichedDriver) => {
    setInputValue(selectedDriver.unitNumber);
    setDriverName(selectedDriver.driverName || '');
    setMuleUnit(selectedDriver.isMule ? selectedDriver.unitNumber : null);
    setValidationStatus('valid');
    setPopupOpen(false);

    reportSelectedDriverValidated(selectedDriver.unitNumber);
    // Don't unlock - the assigned driver value keeps the lock active
    assignDriverWithWarning(selectedDriver.unitNumber);
  };

  /**
   * Open the full drawer with map view
   */
  const handleOpenMapView = () => {
    setPopupOpen(false);
    setDrawerOpen(true);
  };

  /**
   * Handle driver selection from drawer/map
   */
  const handleDrawerDriverSelect = (selectedDriver: EnrichedDriver) => {
    setInputValue(selectedDriver.unitNumber);
    setDriverName(selectedDriver.driverName || '');
    setMuleUnit(selectedDriver.isMule ? selectedDriver.unitNumber : null);
    setValidationStatus('valid');
    setDrawerOpen(false);

    reportSelectedDriverValidated(selectedDriver.unitNumber);
    // Don't unlock - the assigned driver value keeps the lock active
    assignDriverWithWarning(selectedDriver.unitNumber);
  };

  // Get tooltip message for missing dates
  const getTooltipMessage = () => {
    const parts: string[] = [];
    if (origDateMissing && destDateMissing) {
      parts.push('Origin Appointment Date Missing, Destination Appointment Date Missing');
    } else if (origDateMissing) {
      parts.push('Origin Appointment Date Missing');
    } else if (destDateMissing) {
      parts.push('Destination Appointment Date Missing');
    }
    if (hasLinkedLegMissingDates) {
      const legList = linkedLegsMissingDates.join(', ');
      const noun = linkedLegsMissingDates.length === 1 ? 'Leg' : 'Legs';
      parts.push(`Linked ${noun} ${legList} missing appointment date — dispatch group cannot be sent`);
    }
    return parts.join('. ');
  };

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Get border color class based on validation status
   */
  const getBorderColorClass = () => {
    // Dispatch status takes precedence over validation
    if (dispatchStatus === 'success') return 'border-green-500 dark:border-green-600 bg-green-100 dark:bg-green-900/30';
    if (dispatchStatus === 'error') return 'border-red-600 dark:border-red-600 bg-red-100 dark:bg-red-900/30';
    if (validationStatus === 'loading') return 'border-yellow-500 dark:border-yellow-500';
    if (validationStatus === 'valid') return 'border-green-500 dark:border-green-500';
    if (validationStatus === 'invalid') return 'border-red-600 dark:border-red-600';
    return 'border-gray-300 dark:border-gray-600';
  };

  /**
   * Create an Order object for the drawer
   * Note: We only populate the fields needed by DriverDrawer
   */
  const order: Order | null = pronumber ? {
    pronumber,
    legnum,
    customerName,
    originName,
    destinationName,
    originCoordinates,
    destinationCoordinates,
    originAppointmentDate,
    destinationAppointmentDate,
    hazmat,
    isDaycab,
    isTank,
    overweightPermitRequired,
    legs: [],
  } as unknown as Order : null;

  // ============================================================================
  // RENDER
  // ============================================================================

  // A real committed driver unit — excludes blank, the '000' bypass unit, and
  // outside-carrier pseudo-drivers. Gates the Safety System link and the moves
  // drawer, since neither has anything to show for the excluded values.
  const isRealDriverUnit = (value: string | undefined): boolean => {
    const trimmed = (value ?? '').trim();
    return trimmed !== '' && trimmed !== '000' && !isExtraDriverValue(trimmed);
  };

  // The moves drawer only ever lists TODAY's moves, so the icon is dropped on a
  // leg outside that window (e.g. next week's appt, or a departed leg from last
  // month) — otherwise it links to an unrelated day's work. Computed per render
  // rather than memoized so a tab left open past midnight re-evaluates.
  const showMovesIcon = legIsInTodaysMoves({
    origApptDate: originAppointmentDate,
    destApptDate: destinationAppointmentDate,
    departDateTime: legDepartDateTime,
  });

  // When the cell is fully disabled — paid leg, missing access, or any caller-
  // supplied disable — render the driver number plus name (e.g.
  // "5088 - DONONATTO CASTILLO") as static text. Prevents the clear-driver X
  // button (which only checks disabledByPermission) and the Suggest button
  // from leaking through on locked legs. driverName is populated by the
  // validation effect that runs on mount for any assigned driver.
  // The icon links stay available — they're read-only lookups, so a locked leg
  // shouldn't lose the ability to open Safety System or the driver's moves.
  if (disabled || disabledByPermission) {
    const unit = inputValue || driver || '';
    const display = unit
      ? (driverName ? `${unit} - ${driverName}` : unit)
      : '—';
    return (
      <div className={`flex items-start justify-end gap-1 relative ${className}`}>
        <div
          className="px-1 py-0.5 text-xs text-right text-gray-700 dark:text-gray-200 truncate"
          title={display}
          data-field-name="driver_unit_number"
        >
          {display}<MuleBadge isMule={!!unit && muleUnit === unit} />
        </div>
        {isRealDriverUnit(unit) && (
          <DriverIconLinks
            unitNumber={unit.trim()}
            driverName={driverName || undefined}
            highlightPronumber={pronumber}
            showMoves={showMovesIcon}
            className="mt-0.5"
          />
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-start justify-end gap-1 relative ${className}`}>
      <div className="flex items-center gap-0.5">
        {/* User initials for inputs being edited by others */}
        {(() => {
          const editorUser = getInputEditorUser();
          return editorUser ? (
            <Tooltip content={editorUser.name} placement="top">
              <div className="w-5 h-4 flex-shrink-0 flex items-center justify-center text-xs font-semibold" style={{ color: 'rgb(var(--c-violet-500))' }}>
                {editorUser.initials}
              </div>
            </Tooltip>
          ) : null;
        })()}

        {/* Validation Status Icon */}
        {validationStatus === 'loading' && (
          <div className="w-5 h-5 flex-shrink-0">
            <Lottie animationData={loadingAnimation} loop={true} style={{ width: '100%', height: '100%' }} />
          </div>
        )}
        {validationStatus === 'invalid' && (
          <Tooltip
            content={
              rejectionReasons.length > 0
                ? `Not qualified: ${rejectionReasons.join(', ')}`
                : 'Driver not qualified for this move'
            }
          >
            <div className="w-5 h-5 flex-shrink-0 cursor-help">
              <ExclamationCircleIcon className="w-full h-full text-red-600 dark:text-red-600" />
            </div>
          </Tooltip>
        )}
        {validationStatus === 'valid' && (
          <Tooltip content="Driver qualified for this move">
            <div className="w-5 h-5 flex-shrink-0 cursor-help">
              <CheckCircleIcon className="w-full h-full text-green-500 dark:text-green-500" />
            </div>
          </Tooltip>
        )}

        {/* Driver-mismatch indicator — fires when legs in the same dispatch
            group don't share a driver assignment (parent computes via
            describeDispatchGroupDriverConflict). */}
        {groupDriverConflictTooltip && (
          <Tooltip content={groupDriverConflictTooltip}>
            <div className="w-5 h-5 flex-shrink-0 cursor-help">
              <ExclamationTriangleIcon className="w-full h-full text-amber-500 dark:text-amber-400" />
            </div>
          </Tooltip>
        )}

        {/* Override-approved indicator — recorded approval exists for the
            currently-assigned driver on this leg (dispatch_override_approvals).
            Tooltip surfaces the approving user, date, and reason. */}
        {overrideApproval && (
          <Tooltip
            content={(() => {
              const when = (() => {
                const d = new Date(overrideApproval.created_at);
                return isNaN(d.getTime())
                  ? overrideApproval.created_at
                  : d.toLocaleString('en-US', {
                      month: 'numeric',
                      day: 'numeric',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    });
              })();
              const sourceLegnum = overrideApproval.legnum;
              const legSuffix =
                sourceLegnum && sourceLegnum !== legnum
                  ? ` (leg ${sourceLegnum})`
                  : '';
              return `Override approved by ${overrideApproval.username} on ${when}${legSuffix}: ${overrideApproval.reason}`;
            })()}
          >
            <div className="w-5 h-5 flex-shrink-0 cursor-help">
              <ShieldCheckIcon className="w-full h-full text-blue-600 dark:text-blue-400" />
            </div>
          </Tooltip>
        )}

        {/* Driver Input */}
        <div
          className="relative"
          data-field-name="driver_unit_number"
        >
          {showFieldNames && (
            <div className="absolute -top-4 right-0 z-10">
              <FieldNameBadge
                fieldName="driver_unit_number"
                isHighlighted={highlightedFieldId === 'driver_unit_number'}
              />
            </div>
          )}
          <input
            type="text"
            maxLength={7}
            value={getTypingValue() !== null ? getTypingValue()! : inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onMouseDown={(e) => {
              // Start drag-to-fill if handler provided and input has value
              if (onDragMouseDown && inputValue && dragFillKey) {
                e.preventDefault(); // Prevent text selection during drag
                const rect = e.currentTarget.getBoundingClientRect();
                const y = rect.top + window.scrollY;
                onDragMouseDown(dragFillKey, inputValue, y);
              }
            }}
            onMouseOver={(e) => {
              // Track mouse position during drag-to-fill
              if (onDragMouseOver && dragFillActive && dragFillKey) {
                const rect = e.currentTarget.getBoundingClientRect();
                const y = rect.top + window.scrollY;
                onDragMouseOver(dragFillKey, y);
              }
            }}
            disabled={disabled || disabledByPermission || hasMissingDates || isBeingEditedByOther() || dispatchStatus === 'success'}
            title={
              dispatchErrorReason
                ? dispatchErrorReason
                : isBeingEditedByOther()
                ? `Being edited by ${getInputEditorUser()?.name}`
                : hasMissingDates
                ? getTooltipMessage()
                : ''
            }
            style={
              isBeingEditedByOther()
                ? { borderColor: 'rgb(var(--c-violet-500))', cursor: 'not-allowed' }
                : hasMissingDates
                ? { borderColor: 'rgb(var(--c-red-600))', cursor: 'not-allowed' }
                : isDragFillTarget
                ? { borderColor: 'rgb(var(--c-blue-500))' }
                : {}
            }
            data-driver-input={tableType ? 'true' : undefined}
            data-table-type={tableType || undefined}
            data-key={dragFillKey || undefined}
            className={`w-16 px-1 py-0.5 text-xs border-2 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors ${getBorderColorClass()} ${
              highlightedFieldId === 'driver_unit_number'
                ? 'ring-2 ring-amber-500 dark:ring-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.6)]'
                : ''
            } ${isDragFillTarget ? 'ring-2 ring-blue-400' : ''} text-gray-900 dark:text-white ${
              // The state fills are classes rather than an inline backgroundColor
              // so they follow the theme, but that means the resting fill has to
              // stand aside instead of being applied unconditionally - two bg
              // classes on one element are decided by stylesheet order, not by
              // the order they appear here.
              isBeingEditedByOther()
                ? 'bg-violet-100 dark:bg-violet-500/20'
                : hasMissingDates
                ? 'bg-red-100 dark:bg-red-600/20'
                : isDragFillTarget
                ? 'bg-blue-100 dark:bg-blue-500/20'
                : 'bg-white dark:bg-gray-700'
            }`}
            placeholder="Driver"
          />
          {inputValue && !disabledByPermission && (
            <button
              onClick={performClearAttempt}
              className="absolute right-0.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              title="Clear driver"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <MuleBadge isMule={!!inputValue && muleUnit === inputValue} />

      {/* Driver icons — Safety System + moves drawer. Real committed drivers
          get both; the '000' bypass unit and outside-carrier pseudo-drivers get
          a plain silhouette instead (no safety record, no moves to list). */}
      {inputValue && (
        isRealDriverUnit(inputValue) ? (
          <DriverIconLinks
            unitNumber={inputValue.trim()}
            driverName={driverName || undefined}
            highlightPronumber={pronumber}
            showMoves={showMovesIcon}
            className="mt-0.5"
          />
        ) : (
          <Tooltip content={driverName || inputValue} placement="top">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="mt-0.5 w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0 cursor-default"
              fill="currentColor"
            >
              <circle cx="12" cy="6" r="6" />
              <path d="M16.043,14H7.957A4.963,4.963,0,0,0,3,18.957V24H21V18.957A4.963,4.963,0,0,0,16.043,14Z" />
            </svg>
          </Tooltip>
        )
      )}

      {/* Outside-carrier pseudo-driver option — offered when a carrier is set
          on an outside-carrier move. Selecting it assigns the carrier's SCAC /
          derived code as the driver, bypassing qualification (not a real unit).
          Hidden once it's already the assigned value. */}
      {extraDriverCode && !isExtraDriverValue(inputValue) && (
        <button
          type="button"
          onClick={handleSelectExtraDriver}
          disabled={hasMissingDates || disabled || disabledByPermission || isBeingEditedByOther()}
          className="mt-0.5 px-2 py-0.5 text-xs bg-slate-600 hover:bg-slate-700 text-white rounded transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          title={`Use carrier as driver: ${extraDriverCode}`}
        >
          {extraDriverCode} (carrier)
        </button>
      )}

      {/* Suggest Button */}
      <button
        ref={buttonRef}
        onClick={handleFindClick}
        disabled={hasMissingDates || disabled || disabledByPermission || isBeingEditedByOther()}
        className="mt-0.5 px-2 py-0.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        title={
          isBeingEditedByOther()
            ? `Being edited by ${getInputEditorUser()?.name}`
            : hasMissingDates
            ? getTooltipMessage()
            : 'Suggest driver'
        }
      >
        Suggest
      </button>

      {/* Compact Driver Selection Popup — only rendered after first suggest click */}
      {suggestActivated && <DriverCompactPopup
        open={popupOpen}
        onClose={() => {
          setPopupOpen(false);
          // Don't unlock here - let the input value state determine locking
          // The lock stays if there's a value, unlocks if empty
        }}
        position={popupPosition}
        anchorRef={buttonRef}
        drivers={drivers}
        loading={loading}
        error={error}
        onSelectDriver={handleDriverSelect}
        onOpenMapView={handleOpenMapView}
      />}

      {/* Full Driver Drawer with Map — only rendered after first suggest click */}
      {suggestActivated && <DriverDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          // Don't unlock here - let the input value state determine locking
          // The lock stays if there's a value, unlocks if empty
        }}
        order={order}
        drivers={drivers}
        loading={loading}
        error={error}
        onSelectDriver={handleDrawerDriverSelect}
        isMapLoaded={isMapLoaded}
        defaultShowMap={true}
        isPinned={isPinned}
        onPinChange={handlePinChange}
      />}

      {/* Dispatch warning dialog — portaled to escape table stacking contexts */}
      {warningConfirmOpen && dispatchMessages && dispatchMessages.length > 0 && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-scrim/50" onClick={handleWarningCancel} />
          <div
            ref={warningPanelRef}
            style={warningPanelStyle}
            className="relative bg-panel rounded-lg shadow-xl w-full max-w-sm mx-4"
          >
            <ModalHeader
              title={dispatchBlockedReason ? "Dispatch Blocked" : "Dispatch Warning"}
              onClose={handleWarningCancel}
              dragHandleProps={warningDragHandleProps}
            />
            <div className="p-6">
            {dispatchMessages.length === 1 ? (
              // The warning and the question get their own lines. Run together
              // they read as one sentence — "Warning: 2 open Repair Orders Assign
              // driver anyway?" — which is where the eye stumbles.
              <div className="mb-4 space-y-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {dispatchMessages[0]}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {dispatchBlockedReason ? "Resolve the trouble ticket before assigning a driver." : "Assign driver anyway?"}
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{dispatchBlockedReason ? "Resolve the trouble ticket before assigning a driver." : "Assign driver anyway?"}</p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 mb-4 list-disc pl-5 space-y-1">
                  {dispatchMessages.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleWarningCancel}
                className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              {!dispatchBlockedReason && <button
                type="button"
                onClick={handleWarningConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Assign Anyway
              </button>}
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DriverSelectorCell;
